"use client";

import { isTrackablePath } from "@/lib/analytics/paths";
import { currentAnalyticsUrl } from "@/lib/analytics/track";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect, useRef, useState } from "react";

// Los NEXT_PUBLIC_* se incrustan en build (ver docker/web/Dockerfile y
// deploy/.env.build). Sin URL o sin website-id no se pinta nada: un despliegue
// sin analítica configurada no debe romper ni pedir un script inexistente.
const SCRIPT_URL = process.env.NEXT_PUBLIC_UMAMI_SCRIPT_URL;
const WEBSITE_ID = process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID;
const DOMAINS = process.env.NEXT_PUBLIC_UMAMI_DOMAINS;

/**
 * Analítica sin cookies (T7 · KAI-94), limitada a las páginas públicas.
 *
 * Tres decisiones, y las tres cargan peso:
 *
 *  1. El `<Script>` sólo se monta en rutas públicas, así que en una carga
 *     directa de `/dashboard` o de la encuesta el navegador ni siquiera pide
 *     el fichero a stats.kaizenfeedback.com. (Si se llega ahí navegando desde
 *     una pública, la etiqueta que ya estaba sigue en el DOM —`next/script` no
 *     la retira— pero no envía nada: de eso se encarga el punto 2.)
 *  2. `data-auto-track="false"` + pageview manual. Sin esto lo anterior sería
 *     decorativo: el tracker engancha `history.pushState` y, una vez cargado
 *     en una página pública, seguiría contando las navegaciones de cliente
 *     hacia `/dashboard` o `/s/[publicId]`.
 *  3. La `url` se construye aquí y se envía con la forma función. Es
 *     obligatorio, no cosmético: con el auto-track desactivado Umami captura
 *     la URL una sola vez al cargar el script y nunca la refresca, así que un
 *     `track()` a secas registraría TODA la navegación bajo la página de
 *     entrada. Y `track({ url })` no vale — el tracker hace `{...objeto}` y se
 *     lleva por delante `website`, `screen` y `language`. La forma
 *     `track(props => ({ ...props, url }))` es la única que corrige la url
 *     dejando el resto del payload intacto.
 *     Ver https://docs.umami.is/docs/tracker-functions
 */
export default function Analytics() {
  const pathname = usePathname();
  const [loaded, setLoaded] = useState(false);
  // Última url ya contada. Evita el doble pageview cuando volver de una ruta
  // privada a una pública remonta el <Script> (onReady dispara otra vez) a la
  // vez que cambia `pathname`. Guarda también el encadenado del referrer: en
  // una navegación de cliente el referrer correcto es la página anterior, no
  // el referrer externo congelado con el que se cargó el script.
  const lastTracked = useRef(null);
  const trackable = isTrackablePath(pathname);

  useEffect(() => {
    if (!loaded || !trackable) return;

    // Misma puerta que usa el evento «captura-email»: decide sobre la ruta que
    // tiene el navegador ahora, aplica la allowlist de query y devuelve `null`
    // si no procede medir. `pathname` se pasa para exigir que el router y el
    // navegador estén de acuerdo — si no, la navegación está a medias.
    const url = currentAnalyticsUrl(pathname);
    if (!url) return;
    if (lastTracked.current === url) return;

    const previous = lastTracked.current;
    lastTracked.current = url;

    // Si el tracker no llegó a activarse (p.ej. `data-domains` no casa con el
    // host, que es el caso de staging) `window.umami` no existe.
    window.umami?.track((props) => ({
      ...props,
      url,
      // El primer pageview conserva el referrer externo real que trae el
      // tracker; a partir de ahí, la anterior de la casa.
      referrer: previous ?? props.referrer,
    }));
  }, [loaded, trackable, pathname]);

  if (!SCRIPT_URL || !WEBSITE_ID || !trackable) return null;

  return (
    <Script
      src={SCRIPT_URL}
      data-website-id={WEBSITE_ID}
      // Sin data-domains el tracker enviaría pageviews desde cualquier
      // despliegue de esta imagen (staging incluido) al mismo website.
      data-domains={DOMAINS}
      data-auto-track="false"
      // onReady (y no onLoad) porque también dispara cuando el componente se
      // vuelve a montar con el script ya cargado — el camino privada → pública.
      onReady={() => setLoaded(true)}
    />
  );
}
